import { CameraOutlined, PictureOutlined } from "@ant-design/icons";
import { Drawer, Upload, message } from "antd";
import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { svg } from "../Assets/svg";
import { uploadWithThumbnail } from "../helpers/awsUpload";
import {
  getNativePlatform,
  isNativeApp,
  requestCameraPermission,
} from "../helpers/nativePermissions";
import { DecriptEnv } from "../helpers/utils";
import PermissionDeniedModal from "./PermissionDeniedModal";

const { Dragger } = Upload;

const isAndroid = /Android/i.test(navigator.userAgent);

interface S3UploaderProps {
  bucket?: string;
  folder?: string;
  multiple?: boolean;
  showUploadList?: boolean;
  hidden?: boolean;
  onUploaded?: (url: string, key: string) => void;
  onUploadStart?: (file: File) => void;
  onUploadError?: (error: any) => void;
  children?: React.ReactNode;
  disabled?: boolean;
  withThumbnail?: boolean;
  // ✅ New Props for Preview Mode
  image?: string; // The URL of the image to display
  onRemove?: () => void; // Handler for delete icon
  showIcons?: boolean; // Flag to show Replace/Remove icons
  imageDisplayClass?: "cover" | "contain" | "fill" | "scale-down" | "none";
}

export interface S3UploaderRef {
  openPicker: () => void;
}

const S3Uploader = forwardRef<S3UploaderRef, S3UploaderProps>(
  (
    {
      bucket = DecriptEnv(process.env.REACT_APP_AWS_BUCKET_NAME),
      folder = "uploads",
      multiple = false,
      showUploadList = false,
      hidden = false,
      onUploaded,
      onUploadStart,
      onUploadError,
      children,
      disabled,
      withThumbnail = true,
      image,
      onRemove,
      showIcons = false,
      imageDisplayClass = "cover",
    },
    ref,
  ) => {
    const hiddenInputRef = useRef<HTMLInputElement>(null);
    const cameraRef = useRef<HTMLInputElement>(null);
    const [open, setOpen] = useState(false);
    const [dragging, setDragging] = useState(false);
    const [permissionModal, setPermissionModal] = useState<{
      visible: boolean;
      name: string;
    }>({ visible: false, name: "" });

    const processUpload = async (
      file: File,
      onSuccess?: any,
      onError?: any,
    ) => {
      setPermissionModal({ visible: false, name: "" })
      const isImage = file.type.startsWith("image/");
      if (!isImage) {
        message.error("Only image files are allowed!");
        onError?.(new Error("Invalid file type"));
        return;
      }

      try {
        if (onUploadStart) onUploadStart(file);

        const result = await uploadWithThumbnail(
          file,
          bucket,
          folder,
          withThumbnail,
        );

        message.success("Image uploaded!");
        onSuccess?.(result);

        if (onUploaded) {
          onUploaded(result.Location, result.Key);
        }
      } catch (err: any) {
        console.error(err);
        message.error("Upload failed.");
        onError?.(err);
        if (onUploadError) onUploadError(err);
      }
    };

    const processMultipleFiles = async (files: FileList | null) => {
      setPermissionModal({ visible: false, name: "" })
      if (!files || files.length === 0) return;

      const fileArray = Array.from(files);

      for (const file of fileArray) {
        const isImage = file.type.startsWith("image/");
        if (!isImage) {
          message.error("Only image files are allowed!");
          if (onUploadError) onUploadError(new Error("Invalid file type"));
          continue;
        }

        try {
          if (onUploadStart) onUploadStart(file);

          const result = await uploadWithThumbnail(
            file,
            bucket,
            folder,
            withThumbnail,
          );

          message.success("Image uploaded!");

          if (onUploaded) {
            onUploaded(result.Location, result.Key);
          }
        } catch (err: any) {
          console.error(err);
          message.error("Upload failed.");
          if (onUploadError) onUploadError(err);
        }
      }
    };

    const openPicker = () => {
      if (isAndroid) {
        setOpen(true);
      } else if (isNativeApp() && getNativePlatform() === "ios") {
        handleIOSPicker();
      } else {
        hiddenInputRef.current && hiddenInputRef.current.click();
      }
    };

    const handleIOSPicker = async () => {
      const granted = await requestCameraPermission();
      if (!granted) {
        setPermissionModal({ visible: true, name: "Camera" });
        return;
      }
      setTimeout(() => {
        hiddenInputRef.current && hiddenInputRef.current.click();
      }, 100);
    };

    useImperativeHandle(ref, () => ({
      openPicker: openPicker,
    }));

    // --- 1. HIDDEN MODE (Pure Logic) ---
    if (hidden) {
      return isAndroid ? (
        <>
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: "none" }}
            multiple={multiple}
            onChange={(e) => {
              processMultipleFiles(e.target.files);
              e.target.value = "";
            }}
          />
          <input
            ref={hiddenInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            multiple={multiple}
            onChange={(e) => {
              processMultipleFiles(e.target.files);
              e.target.value = "";
            }}
          />
          {open && (
            <AndroidDrawer
              open={open}
              setOpen={setOpen}
              cameraClick={async () => {
                setOpen(false);
                const granted = await requestCameraPermission();
                if (!granted) {
                  setPermissionModal({ visible: true, name: "Camera" });
                  return;
                }
                cameraRef.current && cameraRef.current?.click();
              }}
              galleryClick={async () => {
                setOpen(false);
                hiddenInputRef.current && hiddenInputRef.current.click();
              }}
            />
          )}
          <PermissionDeniedModal
            isModalOpen={permissionModal.visible}
            handleClose={() => setPermissionModal({ visible: false, name: "" })}
            permissionName={permissionModal.name}
          />
        </>
      ) : (
        <input
          ref={hiddenInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          multiple={multiple}
          disabled={disabled}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) processUpload(file);
          }}
        />
      );
    }

    // --- 2. PREVIEW MODE (Image + Actions) ---
    if (image) {
      return (
        <div className="w-100 h-100 position-absolute top-0 start-0">
          <img
            src={image}
            alt="Preview"
            style={{
              width: "100%",
              height: "100%",
              objectFit: imageDisplayClass ? imageDisplayClass : "cover", // or 'cover' based on preference
              borderRadius: "8px",
            }}
          />

          {/* Hidden input for "Replace" functionality */}
          {isAndroid ? (
            <>
              <input
                ref={cameraRef}
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: "none" }}
                multiple={multiple}
                onChange={(e) => {
                  processMultipleFiles(e.target.files);
                  e.target.value = "";
                }}
              />
              <input
                ref={hiddenInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                multiple={multiple}
                onChange={(e) => {
                  processMultipleFiles(e.target.files);
                  e.target.value = "";
                }}
              />
              {open && (
                <AndroidDrawer
                  open={open}
                  setOpen={setOpen}
                  cameraClick={async () => {
                    setOpen(false);
                    const granted = await requestCameraPermission();
                    if (!granted) {
                      setPermissionModal({ visible: true, name: "Camera" });
                      return;
                    }
                    cameraRef.current && cameraRef.current?.click();
                  }}
                  galleryClick={async () => {
                    setOpen(false);
                    hiddenInputRef.current && hiddenInputRef.current.click();
                  }}
                />
              )}
              <PermissionDeniedModal
                isModalOpen={permissionModal.visible}
                handleClose={() => setPermissionModal({ visible: false, name: "" })}
                permissionName={permissionModal.name}
              />
            </>
          ) : (
            <input
              ref={hiddenInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) processUpload(file);
              }}
            />
          )}

          {/* Action Icons */}
          {showIcons && !disabled && (
            <div
              className="position-absolute d-flex gap-2"
              style={{ top: 10, right: 10, zIndex: 10 }}
            >
              {/* Replace Icon */}
              <div
                className="cursor-pointer"
                onClick={openPicker}
                title="Replace Image"
              >
                {svg?.replaceS3ImageIcon}
              </div>

              {/* Remove Icon */}
              <div
                className="cursor-pointer"
                onClick={onRemove}
                title="Remove Image"
              >
                {svg?.deleteS3ImageIcon}
              </div>
            </div>
          )}
        </div>
      );
    }

    // --- 3. UPLOAD MODE (Dragger) ---
    return (
      <>
        {isAndroid ? (
          <>
            <input
              ref={cameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: "none" }}
              multiple={multiple}
              onChange={(e) => {
                processMultipleFiles(e.target.files);
                e.target.value = "";
              }}
            />
            <input
              ref={hiddenInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              multiple={multiple}
              onChange={(e) => {
                processMultipleFiles(e.target.files);
                e.target.value = "";
              }}
            />
            <div
              onClick={(e) => {
                if (isAndroid) {
                  e.preventDefault();
                  e.stopPropagation();
                  setOpen(true);
                }
              }}
              onDragEnter={() => {
                if (isAndroid) setDragging(true);
              }}
              onDragLeave={() => {
                if (isAndroid) setDragging(false);
              }}
              onDrop={() => {
                if (isAndroid) setDragging(false);
              }}
            >
              <Dragger
                accept="image/*"
                multiple={multiple}
                showUploadList={showUploadList}
                customRequest={(options) =>
                  processUpload(
                    options.file as File,
                    options.onSuccess,
                    options.onError,
                  )
                }
                disabled={isAndroid && !dragging}
                className="transparent-dragger"
              >
                {children}
              </Dragger>
            </div>
          </>
        ) : (
          <div
            onClick={async (e) => {
              if (isNativeApp() && getNativePlatform() === "ios") {
                e.preventDefault();
                e.stopPropagation();
                await handleIOSPicker();
              }
            }}
          >
            <Dragger
              accept="image/*"
              multiple={multiple}
              showUploadList={showUploadList}
              customRequest={(options) =>
                processUpload(
                  options.file as File,
                  options.onSuccess,
                  options.onError,
                )
              }
              disabled={disabled}
              className="transparent-dragger"
            >
              {children}
            </Dragger>
          </div>
        )}
        {open && (
            <AndroidDrawer
              open={open}
              setOpen={setOpen}
              cameraClick={async () => {
                setOpen(false);
                const granted = await requestCameraPermission();
                if (!granted) {
                  setPermissionModal({ visible: true, name: "Camera" });
                  return;
                }
                cameraRef.current && cameraRef.current?.click();
              }}
              galleryClick={async () => {
                setOpen(false);
                hiddenInputRef.current && hiddenInputRef.current.click();
              }}
            />
        )}
        <PermissionDeniedModal
          isModalOpen={permissionModal.visible}
          handleClose={() => setPermissionModal({ visible: false, name: "" })}
          permissionName={permissionModal.name}
        />
      </>
    );
  },
);

type AndroidDrawerProps = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  cameraClick: () => void;
  galleryClick?: () => void;
  inputClick?: () => void;
};

export const AndroidDrawer = ({
  open,
  setOpen,
  cameraClick,
  galleryClick,
  inputClick,
}: AndroidDrawerProps) => {
  const handleGallery = galleryClick ?? inputClick ?? (() => {});
  return (
    <Drawer
      placement="bottom"
      open={open}
      onClose={() => setOpen(false)}
      height="auto"
      closable={false}
      maskClosable
    >
      <div className="text-black fw-bolder page-title gap-2 text-center">
        Choose from
      </div>
      <div className="d-flex justify-content-around mt-4">
        <div
          onClick={cameraClick}
          className="d-flex flex-column align-items-center gap-2 fs-6 cursor-pointer"
        >
          <CameraOutlined style={{ fontSize: 28 }} />
          <span className="custom-label">Camera</span>
        </div>
        <div
          onClick={handleGallery}
          className="d-flex flex-column align-items-center gap-2 fs-6 cursor-pointer"
        >
          <PictureOutlined style={{ fontSize: 28 }} />
          <span className="custom-label">Gallery</span>
        </div>
      </div>
      <button
        className="btn btn-lg btn-primary px-4 w-100 mt-4"
        onClick={() => setOpen(false)}
      >
        CANCEL
      </button>
    </Drawer>
  );
};

export default S3Uploader;
